import { useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useStore } from "@lib/store";
import { shallow } from "zustand/shallow";

import HTMLRender from '@components/core/HTMLRender';
import 'swiper/css';

import styles from './CircularGallery.module.css';
import NextImage from '@src/components/elements/Image';
import Image from 'next/image';

function parseRating(value) {
    if (typeof value === 'number') return Math.min(5, Math.max(0, value));
    if (typeof value === 'string') {
        const normalized = value.toLowerCase().trim();
        const numeric = parseFloat(normalized);
        if (!Number.isNaN(numeric)) return Math.min(5, Math.max(0, numeric));
    }
    return 0;
}

const stripTags = value =>
    typeof value === 'string' ? value.replace(/<[^>]*>/g, ' ').trim() : value;

const hasSortValue = (value) => value !== undefined && value !== null && value !== '';

const compareSortValues = (a, b) => {
    const aNum = Number(a);
    const bNum = Number(b);
    const aIsNum = Number.isFinite(aNum);
    const bIsNum = Number.isFinite(bNum);

    if (aIsNum && bIsNum) return aNum - bNum;
    return String(a).localeCompare(String(b));
};

export default function CircularGallery({ items = [] }) {
    const store = useStore(
        (store) => ({
            dir: store.direction,
        }),
        shallow
    );

    const reviews = useMemo(() => {
        const list = Array.isArray(items) ? items : [];
        return list
            .map((item, index) => ({
                ...item,
                id: hasSortValue(item.id) ? item.id : index,
                ratingValue: parseRating(item.rating || stripTags(item.ratingText))
            }))
            .map(item => ({
                ...item,
                order: hasSortValue(item.order) ? item.order : item.id
            }))
            .sort((a, b) => {
                return compareSortValues(a.order, b.order);
            });
    }, [items]);

    if (!reviews.length) return null;

    return (
        <div className={styles.carousel}>
            <Swiper
                dir={store ? store.dir : "ltr"}
                grabCursor
                spaceBetween={20}
                className={styles.swiper}
                breakpoints={{
                    0: { slidesPerView: 1.5, centeredSlides: true },
                    640: { slidesPerView: 2, centeredSlides: true },
                    1024: { slidesPerView: 4, centeredSlides: false },
                }}
            >
                {reviews.map(item => (
                    <SwiperSlide
                        key={item.id}
                        className={styles.swiperSlide}
                        onClick={() => {
                            if (item.reviewUrl) {
                                window.open(item.reviewUrl, '_blank', 'noopener,noreferrer');
                            }
                        }}
                    >
                        <div className={styles.single} role={item.reviewUrl ? 'link' : undefined}>
                            {item.ratingText ? (
                                <div className={styles.rating}>
                                    <HTMLRender data={item.ratingText} />
                                </div>
                            ) : null}
                            <div className={styles.review}>
                                <HTMLRender data={item.review || '--'} />
                            </div>
                            <div className={styles.footer}>
                                {item.logoUrl ? (
                                    <div className={styles.logoWrapper}>
                                        <NextImage
                                            src={item.logoUrl}
                                            alt={item.name}
                                            className={styles.logo}
                                            width={30}
                                            height={30}
                                            quality={100}
                                        />
                                    </div>
                                ) : null}
                                <div className={styles.meta}>
                                    <div className={styles.nameRow}>
                                        <span className={styles.name}>{item.name || 'Reviewer'}</span>
                                        <div className='w-[10px] h-[10px] flex items-center'>
                                            <Image src='/assets/images/reviews-verified-icon.svg' alt="Verified" className={styles.verifiedIcon} width={10} height={10} quality={100} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
}
